import type { RealtimeChannel } from '@supabase/supabase-js'
import { createLogger } from '#logger'
import { defineStore } from 'pinia'

const logger = createLogger('useProjectPresence')

export interface UserPresence {
  id: string
  firstName: string
  lastName: string
  email: string
  lastSeen: string
  isActiveEditor?: boolean
}

// Maps user id to their presence; It's an array of UserPresence objects because
// a user can have multiple connections to the same project (multiple windows/tabs)
export interface PresenceState {
  [key: string]: UserPresence[]
}

export const useProjectPresence = defineStore('project-presence', () => {
  const auth = useAuth()
  const route = useRoute()
  const supabase = useSupabaseClient()
  const projectEditors = useProjectEditors()

  const channel = shallowRef<RealtimeChannel>()
  const presenceState = ref<PresenceState>({})
  const subscribedProjectId = ref<string>()
  let subscribing: Promise<void> | null = null

  function resolveProjectId() {
    const id = route.params.projectId
    return (Array.isArray(id) ? id[0] : id) ?? auth.selectedProjectId ?? undefined
  }

  function canSubscribe() {
    const isOwnerOrEditor = auth.project?.isAdvised && (auth.isOrganizationOwner || projectEditors.canRequestEdit)
    return auth.isAdvisor || !!isOwnerOrEditor
  }

  async function unsubscribe() {
    const activeChannel = channel.value
    channel.value = undefined
    subscribedProjectId.value = undefined
    presenceState.value = {}

    if (activeChannel) {
      await supabase.removeChannel(activeChannel)
    }
  }

  async function subscribeToProject(projectId: string) {
    if (!auth.me || subscribedProjectId.value === projectId) {
      return
    }
    if (subscribing) {
      await subscribing
    }

    subscribing = (async () => {
      await unsubscribe()

      // Private channels require the socket to carry the user's JWT (RLS on
      // realtime.messages is scoped `to authenticated`); supabase-js may not have
      // set it yet on first load, so do it explicitly before subscribing.
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.access_token) {
        await supabase.realtime.setAuth(session.access_token)
      }

      const channelName = `project:${projectId}:presence`
      const presenceChannel = supabase.channel(channelName, {
        config: {
          // Private channel: required on hosted Supabase (Realtime Authorization),
          // where presence only relays if RLS on realtime.messages allows it.
          // See migration ..._realtime_project_presence_rls.sql
          private: true,
          presence: {
            key: auth.me!.id,
          },
        },
      })
      channel.value = presenceChannel

      const userPresence: UserPresence = {
        id: auth.me!.id,
        firstName: auth.me!.firstName ?? '',
        lastName: auth.me!.lastName ?? '',
        email: auth.me!.email,
        lastSeen: new Date().toISOString(),
        isActiveEditor: projectEditors.isActiveEditor,
      }

      presenceChannel
        // sync carries the FULL state on every join/leave, so it's the only event we need.
        .on('presence', { event: 'sync' }, () => {
          presenceState.value = structuredClone(presenceChannel.presenceState()) as PresenceState
        })
        .subscribe(async (status) => {
          if (status === 'SUBSCRIBED') {
            subscribedProjectId.value = projectId
            const trackStatus = await presenceChannel.track(userPresence)
            if (trackStatus !== 'ok') {
              logger.warn('Failed to track presence on channel', { channelName, trackStatus })
            }
          }
          else if (status === 'CHANNEL_ERROR' || status === 'CLOSED') {
            subscribedProjectId.value = undefined
          }
        })
    })()

    try {
      await subscribing
    }
    finally {
      subscribing = null
    }
  }

  async function syncSubscription() {
    const excluded = route.path.startsWith('/academy') || route.path.startsWith('/public')
    const projectId = resolveProjectId()
    const isProjectRoute = route.path.startsWith('/project/') && !!projectId

    if (excluded || !isProjectRoute || !auth.me?.id) {
      await unsubscribe()
      return
    }

    if (!projectEditors.isInitialized) {
      await projectEditors.initializeFromApi()
    }

    if (!canSubscribe()) {
      await unsubscribe()
      return
    }

    try {
      await subscribeToProject(projectId)
    }
    catch (error) {
      logger.warn('Failed to sync project presence subscription', { err: error })
    }
  }

  if (import.meta.client) {
    watch(
      () => ({
        projectId: resolveProjectId(),
        userId: auth.me?.id,
        path: route.path,
      }),
      () => {
        void syncSubscription()
      },
      { immediate: true },
    )
  }

  const presentUsers = computed(() => {
    return Object.values(presenceState.value)
      .map((metas) => {
        const first = metas[0]!
        const lastSeenMax = metas.reduce(
          (max, m) => Math.max(max, m.lastSeen ? new Date(m.lastSeen).getTime() : 0),
          0,
        )

        return {
          ...first,
          firstName: first.firstName || null,
          lastName: first.lastName || null,
          // Indicates how many times the user has connected to the project; Could be different tabs/windows
          connections: metas.length,
          lastSeenMax,
        }
      })
      .sort((a, b) => b.lastSeenMax - a.lastSeenMax)
  })

  return {
    presenceState,
    presentUsers,
    subscribeToProject,
    unsubscribe,
  }
})
